import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ControlItemValue } from '../../../types/types';
import {
  CSV,
  CUSTOM,
  JSON_FORMAT,
  SEMICOLON,
  TAB,
  TXT,
  XLSX,
  XML,
} from '../constants';
import {
  DecimalSeparatorMode,
  DelimiterMode,
  ExportEncoding,
  ExportFormat,
  JsonValueMode,
} from '../types';
import isDelimiterSupported from '../utils/isDelimiterSupported';
import isForbiddenDelimiter from '../utils/isForbiddenDelimiter';

type UseExportOptionsParams = {
  isWithSubsetsMode: boolean;
};

export type UseExportOptionsResult = {
  format: ExportFormat;
  encoding: ExportEncoding;
  delimiterMode: DelimiterMode;
  customDelimiter: string;
  decimalSeparator: DecimalSeparatorMode;
  jsonValueMode: JsonValueMode;
  includeColumnNames: boolean;
  useDisplayFormatting: boolean;
  header: string;
  footer: string;
  exportAll: boolean;
  isCustomDelimiterUnsupported: boolean;
  isEmptyCustomDelimiterRejected: boolean;
  checkCustomDelimiterOnSubmit: () => boolean;
  setExportAll: Dispatch<SetStateAction<boolean>>;
  setIncludeColumnNames: Dispatch<SetStateAction<boolean>>;
  setUseDisplayFormatting: Dispatch<SetStateAction<boolean>>;
  handleFormatChange: (value: ControlItemValue) => void;
  handleEncodingChange: (value: ControlItemValue) => void;
  handleDelimiterModeChange: (value: ControlItemValue) => void;
  handleDecimalSeparatorChange: (value: ControlItemValue) => void;
  handleJsonValueModeChange: (value: ControlItemValue) => void;
  handleCustomDelimiterChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleHeaderChange: (value: string) => void;
  handleFooterChange: (value: string) => void;
};

export const useExportOptions = ({
  isWithSubsetsMode,
}: UseExportOptionsParams): UseExportOptionsResult => {
  const [format, setFormat] = useState<ExportFormat>(XLSX);
  const [encoding, setEncoding] = useState<ExportEncoding>('utf-8');
  const [delimiterMode, setDelimiterMode] = useState<DelimiterMode>(TAB);
  const [customDelimiter, setCustomDelimiter] = useState<string>(';');
  const [isEmptyCustomDelimiterRejected, setIsEmptyCustomDelimiterRejected] =
    useState<boolean>(false);
  const [decimalSeparator, setDecimalSeparator] =
    useState<DecimalSeparatorMode>('display');
  const [jsonValueMode, setJsonValueMode] = useState<JsonValueMode>('string');
  const [includeColumnNames, setIncludeColumnNames] = useState<boolean>(true);
  const [useDisplayFormatting, setUseDisplayFormatting] =
    useState<boolean>(true);
  const [header, setHeader] = useState<string>('');
  const [footer, setFooter] = useState<string>('');
  const [exportAll, setExportAll] = useState<boolean>(false);

  useEffect(() => {
    if (isWithSubsetsMode && format !== XML && format !== JSON_FORMAT) {
      // Combined master/subset exports support only hierarchical formats.
      setFormat(XML);
    }
  }, [isWithSubsetsMode, format]);

  const handleFormatChange = useCallback(
    (value: ControlItemValue): void => {
      const nextFormat = value as ExportFormat;

      if (
        isWithSubsetsMode &&
        nextFormat !== XML &&
        nextFormat !== JSON_FORMAT
      ) {
        return;
      }

      setFormat(nextFormat);

      if (nextFormat === CSV) {
        // CSV defaults to comma, but the delimiter stays user-selectable.
        setDelimiterMode(SEMICOLON);
      }

      if (nextFormat === TXT) {
        // TXT keeps the legacy tab-delimited default from the examples.
        setDelimiterMode(TAB);
      }
    },
    [isWithSubsetsMode]
  );

  const handleEncodingChange = useCallback((value: ControlItemValue): void => {
    setEncoding(value as ExportEncoding);
  }, []);

  const handleDelimiterModeChange = useCallback(
    (value: ControlItemValue): void => {
      setDelimiterMode(value as DelimiterMode);
    },
    []
  );

  const handleDecimalSeparatorChange = useCallback(
    (value: ControlItemValue): void => {
      setDecimalSeparator(value as DecimalSeparatorMode);
    },
    []
  );

  const handleJsonValueModeChange = useCallback(
    (value: ControlItemValue): void => {
      setJsonValueMode(value as JsonValueMode);
    },
    []
  );

  const handleCustomDelimiterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setCustomDelimiter(event.target.value);
      setIsEmptyCustomDelimiterRejected(false);
    },
    []
  );

  const handleHeaderChange = useCallback((value: string): void => {
    setHeader(value);
  }, []);

  const handleFooterChange = useCallback((value: string): void => {
    setFooter(value);
  }, []);

  // Mirrors the panel's own visibility condition for the field, so gating and rendering cannot drift apart.
  const isCustomDelimiterActive = useMemo<boolean>(
    () =>
      !isWithSubsetsMode &&
      (format === CSV || format === TXT) &&
      delimiterMode === CUSTOM,
    [isWithSubsetsMode, format, delimiterMode]
  );

  // An empty delimiter is a soft block reported on submit, so it must not disable the save button.
  const isCustomDelimiterUnsupported = useMemo<boolean>(() => {
    if (!isCustomDelimiterActive || !customDelimiter) return false;
    if (isForbiddenDelimiter(customDelimiter, format)) return true;

    return !isDelimiterSupported(customDelimiter, encoding);
  }, [isCustomDelimiterActive, customDelimiter, encoding, format]);

  const checkCustomDelimiterOnSubmit = useCallback((): boolean => {
    const isEmpty = isCustomDelimiterActive && !customDelimiter;
    setIsEmptyCustomDelimiterRejected(isEmpty);

    return !isEmpty;
  }, [isCustomDelimiterActive, customDelimiter]);

  return {
    format,
    encoding,
    delimiterMode,
    customDelimiter,
    decimalSeparator,
    jsonValueMode,
    includeColumnNames,
    useDisplayFormatting,
    header,
    footer,
    exportAll,
    isCustomDelimiterUnsupported,
    isEmptyCustomDelimiterRejected,
    checkCustomDelimiterOnSubmit,
    setExportAll,
    setIncludeColumnNames,
    setUseDisplayFormatting,
    handleFormatChange,
    handleEncodingChange,
    handleDelimiterModeChange,
    handleDecimalSeparatorChange,
    handleJsonValueModeChange,
    handleCustomDelimiterChange,
    handleHeaderChange,
    handleFooterChange,
  };
};
